import { useCallback, useMemo, useRef } from 'react';
import {
  Badge,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FilmIcon,
  ImageIcon,
  LayoutGridIcon,
  LockIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import {
  CREATIVE_FIELD_PREFIX,
  NODE_ACTION_LABELS,
  fieldDef,
  levelLabel,
  levelPluralLabel,
  nodeFieldValue,
  nodeIssues,
  isNodeFieldEditable,
  isNodePublished,
  useJobAsset,
  type JobFieldValue,
  clashesWithExisting,
  type JobNode,
  type JobPlatformGroup,
  type NodeAction,
  type PlatformFieldDef,
  type PlatformLevel,
} from '@/entities/media-campaign';
import {
  ancestorLevelsOf,
  ancestorSpans,
  buildSheetRows,
  countSeverity,
  leafLevelOf,
  leafRows,
  type LeafRow,
  levelFieldColumns,
  nodeRowIssues,
} from '../lib/flatten';
import { MIN_NARROW_COLUMN_WIDTH } from '../lib/column-widths';
import { type PreflightStatus } from '../lib/preflight';
import { useColumnWidths } from '../lib/useColumnWidths';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { JobTableCell } from './JobTableCell';
import { platformStatusLabel } from './delivery-labels';
import { useTranslation } from 'react-i18next';

/**
 * Spreadsheet-style gutter, roughly square against the row height. The only
 * column that cannot be resized: it holds a counter two or three digits wide,
 * so there is nothing to reveal by widening it.
 */
const ROW_NUMBER_COLUMN_WIDTH = 44;
const NAME_COLUMN_WIDTH = 300;
const ACTION_COLUMN_WIDTH = 152;
const CREATIVE_COLUMN_WIDTH = 76;
const ANCESTOR_COLUMN_WIDTH = 200;

/** Indent per level in the grouped view, so an ad set nests inside its campaign. */
const BAND_INDENT = 20;

/**
 * Synthetic keys for the columns that are not platform fields, so they can be
 * resized and remembered through the same store. Prefixed to keep them out of the
 * namespace a platform's own field keys occupy.
 */
/* eslint-disable i18next/no-literal-string -- synthetic column keys and the platform's own
   `name` field key: lookup identifiers the resize store and schema match on, not copy */
const ACTION_COLUMN_KEY = '__action';
const PREVIEW_COLUMN_KEY = '__preview';
const NAME_FIELD_KEY = 'name';
/* eslint-enable i18next/no-literal-string */

/** Shared empty lookups, so a band row does not allocate two maps per render. */
const EMPTY_ISSUES: ReadonlyMap<string, ReturnType<typeof nodeIssues>[number]> = new Map();
const EMPTY_FIELDS: ReadonlyMap<string, JobFieldValue> = new Map();

const ACTION_VARIANT: Record<NodeAction, 'positive' | 'secondary' | 'ghost'> = {
  create: 'positive',
  update: 'secondary',
  unchanged: 'ghost',
};

/** "3", or "3-4" when a line was assembled from a contiguous block of rows. */
function formatSourceRows(rows: number[]): string {
  if (rows.length === 0) return '';
  const sorted = [...rows].sort((a, b) => a - b);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const isContiguous = last - first === sorted.length - 1;
  return sorted.length === 1 ? `${first}` : isContiguous ? `${first}-${last}` : sorted.join(', ');
}

function creativeIcon(creative: string | null) {
  if (creative === null) return LayoutGridIcon;
  if (/\.(mp4|mov|webm)$/i.test(creative) || /video/i.test(creative)) return FilmIcon;
  if (/\.(jpe?g|png|gif|webp)$/i.test(creative)) return ImageIcon;
  return LayoutGridIcon;
}

/**
 * The creative itself where the plan brought one, an icon where it only named one.
 *
 * A plan uploaded as a bare sheet still just names its asset, and there is no
 * thumbnail in any platform schema — so the kind is inferred from the file name and
 * drawn as an icon. A zip upload carries the bytes, and then the row can show the
 * actual picture, which is the only way to catch the right ad wearing the wrong
 * creative. A video row draws the thumbnail it names, which is a still of the same
 * creative and the nearest thing to the video a 36px cell could show. The creative's
 * own fields sit on the ad row behind `CREATIVE_FIELD_PREFIX`, so a bare `creative`
 * key matches nothing.
 */
function CreativeThumbnail({ jobId, node }: { jobId: string; node: JobNode }) {
  const imageFile = nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}imageFile`);
  const namesVideo = Boolean(
    nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}videoFile`) ??
    nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}youtubeVideoUrl`),
  );
  const thumbnailFile = nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}thumbnailFile`);
  const { data: src } = useJobAsset(
    jobId,
    imageFile ?? (namesVideo ? thumbnailFile : null),
    'image',
  );
  // A video row with no thumbnail has nothing to fetch, so it would otherwise fall through
  // to the same icon as a row with no creative at all; naming a video is worth its own.
  const Icon = namesVideo
    ? FilmIcon
    : creativeIcon(nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}name`));

  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="size-9 shrink-0 rounded-md object-cover"
        data-testid={`job-thumbnail-${node.id}`}
      />
    );
  }

  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
    </div>
  );
}

function IssueBadge({
  errors,
  warnings,
  onClick,
  testId,
}: {
  errors: number;
  warnings: number;
  onClick: () => void;
  testId: string;
}) {
  const { t } = useTranslation('projects');
  if (errors + warnings === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('deliveryJobReview.jobPlatformTable.issuesHere', { count: errors + warnings })}
      data-testid={testId}
      className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        errors > 0
          ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
          : 'text-warning-foreground hover:bg-accent'
      }`}
    >
      <TriangleAlertIcon className="size-3.5 shrink-0" aria-hidden />
      {errors + warnings}
    </button>
  );
}

/**
 * The account already carries this name, and publishing would make a second object
 * rather than change the first.
 *
 * A badge of its own beside the action one rather than folded into it, because they
 * answer different questions: what the plan intends to do, and what is already out
 * there. Clicking opens the findings, where linking the row to the existing object is
 * the deliberate answer.
 */
function ClashBadge({ node, onOpenIssues }: { node: JobNode; onOpenIssues: () => void }) {
  const { t } = useTranslation('projects');
  if (!clashesWithExisting(node)) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onOpenIssues} data-testid={`job-row-clash-${node.id}`}>
          <Badge variant="warning" roundness="full" className="font-normal">
            <TriangleAlertIcon className="size-3" aria-hidden />
            {t('deliveryJobReview.jobPlatformTable.alsoOnPlatform')}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {t('deliveryJobReview.jobPlatformTable.alsoOnPlatformDetail')}
      </TooltipContent>
    </Tooltip>
  );
}

export interface JobPlatformTableProps {
  /** Needed to fetch the creative images the plan was uploaded with. */
  jobId: string;
  group: JobPlatformGroup;
  /** Bands per campaign and ad set instead of parent name columns. */
  grouped: boolean;
  collapsed: ReadonlySet<string>;
  onToggleCollapsed: (nodeId: string) => void;
  onEditField: (nodeId: string, key: string, value: string | null) => void;
  /** Opens the side drawer with a campaign's or ad set's full field list. */
  onOpenDetails: (node: JobNode) => void;
  onOpenIssues: (nodeId: string) => void;
  onPreviewCreative: (node: JobNode) => void;
  /** `nodeId:fieldKey` of the cell the drawer just sent us to. */
  focusedCell: string | null;
  /** False until pre-flight has run: a fresh plan shows no red at all. */
  showIssues: boolean;
  preflightStatus: PreflightStatus;
}

/**
 * One platform's whole plan as a flat sheet: a row per leaf object, with the
 * campaign and ad set it belongs to as name-only columns.
 *
 * Flat because the source spreadsheet is flat, so a row here lines up with a row
 * there. Parent CONFIGURATION is deliberately absent — it does not fit the leaf
 * columns, and inlining it turned the sheet into noise — so the parent names are
 * links into the details drawer instead.
 *
 * A raw <table> on purpose — Tessra's <Table> wraps it in its own
 * `overflow-x-auto` div, which shrink-wraps to the content, steals the
 * horizontal scrollbar up under the last row, and becomes the scroll ancestor
 * that `position: sticky` resolves against.
 */
export function JobPlatformTable({
  jobId,
  group,
  grouped,
  collapsed,
  onToggleCollapsed,
  onEditField,
  onOpenDetails,
  onOpenIssues,
  onPreviewCreative,
  focusedCell,
  showIssues,
  preflightStatus,
}: JobPlatformTableProps) {
  const { t } = useTranslation('projects');
  const platform = group.platform;
  const leafLevel = leafLevelOf(platform);
  const { widthOf, setWidth, resetWidth } = useColumnWidths(platform);

  /**
   * A permanently stable commit callback.
   *
   * The cells are memoized, and a resize re-renders this component at pointer
   * rate — so a fresh closure per cell per render would defeat the memo on every
   * frame. The ref keeps the identity fixed without ever going stale.
   */
  const editFieldRef = useRef(onEditField);
  editFieldRef.current = onEditField;
  const commitField = useCallback(
    (nodeId: string, fieldKey: string, value: string | null) =>
      editFieldRef.current(nodeId, fieldKey, value),
    [],
  );

  // Split from the widths deliberately: the schema half is the expensive half and
  // does not change while a column is being dragged.
  const baseColumns = useMemo(() => levelFieldColumns(platform, leafLevel), [platform, leafLevel]);
  // The schema's default width unless this reviewer has dragged this column before.
  const columns = baseColumns.map((column) => ({
    ...column,
    width: widthOf(leafLevel, column.fieldKey, column.width),
  }));
  // The platform's own definition for the title column, so the name cell validates
  // and labels itself from the schema like every other field. Falls back only if a
  // platform somehow omits it, which would be a schema bug rather than a plan one.
  const nameFieldDef: PlatformFieldDef = useMemo(
    () =>
      fieldDef(platform, leafLevel, NAME_FIELD_KEY) ?? {
        key: NAME_FIELD_KEY,
        label: levelLabel(platform, leafLevel),
        kind: 'text',
        required: true,
      },
    [platform, leafLevel],
  );
  // Grouping already says which campaign a row belongs to, so the parent name
  // columns would only repeat the band above them.
  const ancestors = useMemo(() => (grouped ? [] : ancestorLevelsOf(platform)), [grouped, platform]);

  // Every column except the row gutter is resizable, so each reads its width from
  // the same store. The frozen columns' left edges follow from the live widths
  // rather than the constants, or dragging the name column would leave the ones
  // pinned after it overlapping.
  const nameWidth = widthOf(leafLevel, NAME_FIELD_KEY, NAME_COLUMN_WIDTH);
  const actionWidth = widthOf(leafLevel, ACTION_COLUMN_KEY, ACTION_COLUMN_WIDTH);
  const previewWidth = widthOf(leafLevel, PREVIEW_COLUMN_KEY, CREATIVE_COLUMN_WIDTH);
  const ancestorWidth = (level: PlatformLevel) =>
    widthOf(level, NAME_FIELD_KEY, ANCESTOR_COLUMN_WIDTH);

  const nameStickyLeft = ROW_NUMBER_COLUMN_WIDTH;
  const rows = useMemo(
    () => (grouped ? buildSheetRows(group, platform, collapsed) : leafRows(group, platform)),
    [grouped, group, platform, collapsed],
  );

  // Merged parent cells: one campaign name across its twelve ads rather than twelve
  // copies of it, and one copy of its findings rather than twelve.
  const parentSpans = useMemo(
    () =>
      ancestors.length === 0
        ? new Map<string, Partial<Record<PlatformLevel, number>>>()
        : ancestorSpans(rows as LeafRow[], ancestors),
    [rows, ancestors],
  );

  // What a reader counts: the first row on screen is 1. The uploaded sheet's own
  // numbering starts at 4 under the template's banner, bands and labels, which read
  // as an off-by-three bug here — and the link back to the file is not lost, since
  // every finding already quotes its source row and the cell carries it on hover.
  //
  // Bands are skipped rather than numbered: they span the full width and are about
  // a parent object, not a line of the plan.
  const rowNumbers = useMemo(() => {
    const numbers = new Map<string, number>();
    for (const row of rows) {
      if ('kind' in row && row.kind === 'band') continue;
      numbers.set(row.id, numbers.size + 1);
    }
    return numbers;
  }, [rows]);

  /**
   * Every row's findings and every node's fields, resolved once per plan.
   *
   * Both lookups scan the level schema and then the node's field list, so doing
   * them inline made the render O(rows x fields x fields). Recomputed only when
   * the plan or the issue toggle changes, not when a column is dragged.
   */
  const rowData = useMemo(() => {
    const data = new Map<
      string,
      {
        errors: number;
        warnings: number;
        issueByField: Map<string, ReturnType<typeof nodeIssues>[number]>;
        fieldByKey: Map<string, JobFieldValue>;
      }
    >();

    for (const row of rows) {
      const node = row.node;
      if (data.has(node.id)) continue;

      const { errors, warnings } = countSeverity(showIssues ? nodeRowIssues(platform, node) : []);
      // A finding with no field is about the row as a whole; it reaches the reader
      // through the row badge, so it has no cell to key against here.
      const issueByField = new Map(
        (showIssues ? nodeIssues(platform, node) : [])
          .filter((issue) => issue.field !== null)
          .map((issue) => [issue.field as string, issue]),
      );
      const fieldByKey = new Map(node.fields.map((field) => [field.key, field]));
      data.set(node.id, { errors, warnings, issueByField, fieldByKey });
    }
    return data;
  }, [rows, platform, showIssues]);
  const columnCount = 4 + ancestors.length + columns.length;

  const totalWidth =
    ROW_NUMBER_COLUMN_WIDTH +
    nameWidth +
    actionWidth +
    previewWidth +
    ancestors.reduce((sum, ancestor) => sum + ancestorWidth(ancestor), 0) +
    columns.reduce((sum, column) => sum + column.width, 0);

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border-light">
      <table
        className="caption-bottom border-separate border-spacing-0 text-sm"
        style={{ tableLayout: 'fixed', width: `max(100%, ${totalWidth}px)` }}
      >
        <TableHeader>
          <TableRow className="hover:!bg-transparent">
            {/* Deliberately unlabelled: a bare counter needs no header. */}
            <TableHead
              className="sticky left-0 top-0 z-40 border-b border-r border-border bg-background p-0"
              style={{ width: `${ROW_NUMBER_COLUMN_WIDTH}px` }}
            >
              <span className="sr-only">{t('deliveryJobReview.jobPlatformTable.row')}</span>
            </TableHead>
            <TableHead
              className="sticky top-0 z-40 border-b border-r border-border bg-background text-muted-foreground"
              style={{ left: `${nameStickyLeft}px`, width: `${nameWidth}px` }}
            >
              <div className="relative">
                <span className="block truncate">{levelLabel(platform, leafLevel)}</span>
                <ColumnResizeHandle
                  label={levelLabel(platform, leafLevel)}
                  width={nameWidth}
                  onResize={(next) => setWidth(leafLevel, NAME_FIELD_KEY, next)}
                  onReset={() => resetWidth(leafLevel, NAME_FIELD_KEY)}
                />
              </div>
            </TableHead>
            <TableHead
              className="sticky top-0 z-30 border-b border-r border-border bg-background text-muted-foreground"
              style={{ width: `${actionWidth}px` }}
            >
              <div className="relative">
                <span className="block truncate">
                  {t('deliveryJobReview.jobPlatformTable.change')}
                </span>
                <ColumnResizeHandle
                  label={t('deliveryJobReview.jobPlatformTable.change')}
                  width={actionWidth}
                  onResize={(next) => setWidth(leafLevel, ACTION_COLUMN_KEY, next)}
                  onReset={() => resetWidth(leafLevel, ACTION_COLUMN_KEY)}
                />
              </div>
            </TableHead>
            {ancestors.map((ancestor) => (
              <TableHead
                key={ancestor}
                className="sticky top-0 z-30 border-b border-r border-border bg-surface text-muted-foreground dark:bg-surface-raised"
                style={{ width: `${ancestorWidth(ancestor)}px` }}
              >
                <div className="relative">
                  <span className="block truncate">{levelLabel(platform, ancestor)}</span>
                  <ColumnResizeHandle
                    label={levelLabel(platform, ancestor)}
                    width={ancestorWidth(ancestor)}
                    onResize={(next) => setWidth(ancestor, NAME_FIELD_KEY, next)}
                    onReset={() => resetWidth(ancestor, NAME_FIELD_KEY)}
                  />
                </div>
              </TableHead>
            ))}
            <TableHead
              className="sticky top-0 z-30 border-b border-r border-border bg-background text-center text-muted-foreground"
              style={{ width: `${previewWidth}px` }}
            >
              <div className="relative">
                {/* Not "Creative": several platforms already have a field by that
                    name, and two identical headers is worse than a plain one. */}
                <span className="block truncate">
                  {t('deliveryJobReview.jobPlatformTable.preview')}
                </span>
                <ColumnResizeHandle
                  label={t('deliveryJobReview.jobPlatformTable.preview')}
                  width={previewWidth}
                  minWidth={MIN_NARROW_COLUMN_WIDTH}
                  onResize={(next) => setWidth(leafLevel, PREVIEW_COLUMN_KEY, next)}
                  onReset={() => resetWidth(leafLevel, PREVIEW_COLUMN_KEY)}
                />
              </div>
            </TableHead>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className="sticky top-0 z-30 border-b border-r border-border bg-background text-muted-foreground"
                style={{ width: `${column.width}px` }}
              >
                {/* The label truncates, the header does not clip: the drag handle
                    sits on the border and would be cut off by `overflow-hidden`. */}
                <div className="relative">
                  <span className="block truncate">
                    {column.def.label}
                    {column.def.required && <span className="ml-0.5 text-destructive">*</span>}
                  </span>
                  <ColumnResizeHandle
                    label={column.def.label}
                    width={column.width}
                    onResize={(next) => setWidth(leafLevel, column.fieldKey, next)}
                    onReset={() => resetWidth(leafLevel, column.fieldKey)}
                  />
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row) => {
            const { errors, warnings, issueByField, fieldByKey } = rowData.get(row.node.id) ?? {
              errors: 0,
              warnings: 0,
              issueByField: EMPTY_ISSUES,
              fieldByKey: EMPTY_FIELDS,
            };

            if ('kind' in row && row.kind === 'band') {
              const isOpen = !collapsed.has(row.id);

              return (
                <TableRow
                  key={row.id}
                  className="hover:!bg-transparent"
                  data-testid={`job-band-${row.id}`}
                >
                  {/* One full-width cell: a band is about its own object, not
                      about the leaf columns, so it must not be forced into them. */}
                  <TableCell
                    colSpan={columnCount}
                    className={`border-b border-border p-0 ${
                      row.depth === 0 ? 'bg-surface dark:bg-surface-raised' : 'bg-muted/40'
                    }`}
                  >
                    {/* Pinned on the CONTENT, not the cell. The cell spans the whole
                        table, so sticking it does nothing — it is already under the
                        viewport at every scroll position while its label scrolls off
                        to the left. Sticking the inner row keeps the campaign and ad
                        set names in place, which is the point of naming them.
                        `w-fit` so the sticky box is the label, not the full span. */}
                    <div
                      data-testid={`job-band-label-${row.id}`}
                      className="sticky left-0 flex w-fit max-w-full flex-wrap items-center gap-2 py-2 pr-2"
                      style={{ paddingLeft: `${8 + row.depth * BAND_INDENT}px` }}
                    >
                      <button
                        type="button"
                        onClick={() => onToggleCollapsed(row.id)}
                        aria-expanded={isOpen}
                        data-testid={`job-band-toggle-${row.id}`}
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {isOpen ? (
                          <ChevronDownIcon className="size-4" />
                        ) : (
                          <ChevronRightIcon className="size-4" />
                        )}
                        <span className="sr-only">
                          {isOpen
                            ? t('deliveryJobReview.jobPlatformTable.collapse')
                            : t('deliveryJobReview.jobPlatformTable.expand')}
                        </span>
                      </button>

                      <Badge
                        variant="ghost"
                        roundness="full"
                        className="border-border-light font-normal"
                      >
                        {levelLabel(platform, row.level)}
                      </Badge>

                      <button
                        type="button"
                        onClick={() => onOpenDetails(row.node)}
                        data-testid={`job-band-open-${row.id}`}
                        className="truncate font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {nodeFieldValue(row.node, NAME_FIELD_KEY) ??
                          t('deliveryJobReview.jobPlatformTable.untitled')}
                      </button>

                      <Badge
                        variant={ACTION_VARIANT[row.node.action]}
                        roundness="full"
                        className="font-normal"
                      >
                        {NODE_ACTION_LABELS[row.node.action]}
                      </Badge>

                      <ClashBadge node={row.node} onOpenIssues={() => onOpenIssues(row.id)} />

                      {/* Only while folded, where it says what is hidden rather
                          than repeating what is already on screen. */}
                      {!isOpen && (
                        <span className="text-xs text-muted-foreground">
                          {t('deliveryJobReview.jobPlatformTable.leafCount', {
                            count: row.leaves,
                            noun: (row.leaves === 1
                              ? levelLabel(platform, leafLevel)
                              : levelPluralLabel(platform, leafLevel)
                            ).toLowerCase(),
                          })}
                        </span>
                      )}

                      <IssueBadge
                        errors={errors}
                        warnings={warnings}
                        onClick={() => onOpenIssues(row.id)}
                        testId={`job-row-issues-${row.id}`}
                      />

                      <button
                        type="button"
                        onClick={() => onOpenDetails(row.node)}
                        data-testid={`job-band-details-${row.id}`}
                        className="ml-auto rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {t('deliveryJobReview.jobPlatformTable.details')}
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            }

            return (
              <TableRow
                key={row.id}
                className="group/row text-sm"
                data-testid={`job-row-${row.id}`}
              >
                <TableCell
                  className="sticky left-0 z-10 border-b border-r border-border bg-background px-1 py-1.5 text-center font-mono text-xs tabular-nums text-muted-foreground group-hover/row:bg-accent"
                  style={{ width: `${ROW_NUMBER_COLUMN_WIDTH}px` }}
                  title={
                    row.node.sourceRows.length > 0
                      ? `Row ${formatSourceRows(row.node.sourceRows)} of the uploaded file`
                      : undefined
                  }
                >
                  {rowNumbers.get(row.id)}
                </TableCell>

                {/* Editable like every other field. A name is one of the likeliest
                    things to need fixing — a typo, or a convention the sheet got
                    wrong — and it was the only cell a reviewer could not correct. */}
                <JobTableCell
                  def={nameFieldDef}
                  field={fieldByKey.get(NAME_FIELD_KEY)}
                  editable={isNodeFieldEditable(platform, row.node, NAME_FIELD_KEY)}
                  issue={issueByField.get(NAME_FIELD_KEY)}
                  width={nameWidth}
                  cellId={`${row.node.id}:name`}
                  isFocused={focusedCell === `${row.node.id}:name`}
                  preflightStatus={preflightStatus}
                  nodeId={row.node.id}
                  onCommit={commitField}
                  stickyLeft={nameStickyLeft}
                  paddingLeft={grouped ? 8 + 2 * BAND_INDENT : undefined}
                  adornment={
                    <IssueBadge
                      errors={errors}
                      warnings={warnings}
                      onClick={() => onOpenIssues(row.id)}
                      testId={`job-row-issues-${row.id}`}
                    />
                  }
                />

                <TableCell
                  className="overflow-hidden border-b border-r border-border px-2 py-1.5"
                  style={{ width: `${actionWidth}px` }}
                >
                  <div className="flex flex-wrap items-center gap-1">
                    {isNodePublished(row.node) ? (
                      // What the row IS beats what the file asked for: once it is live
                      // the planned change has already happened.
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="secondary"
                            roundness="full"
                            className="font-normal"
                            data-testid={`job-row-published-${row.node.id}`}
                          >
                            <LockIcon className="size-3" aria-hidden />
                            {t('deliveryJobReview.jobPlatformTable.live')}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('deliveryJobReview.jobPlatformTable.alreadyPublishedReadOnly')}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Badge
                        variant={ACTION_VARIANT[row.node.action]}
                        roundness="full"
                        className="font-normal"
                      >
                        {NODE_ACTION_LABELS[row.node.action]}
                      </Badge>
                    )}
                    <ClashBadge node={row.node} onOpenIssues={() => onOpenIssues(row.id)} />
                  </div>
                </TableCell>

                {/* Flat mode only: parents are name-only and tinted, their
                    configuration living in the details drawer. Grouped mode says
                    the same thing with the band above. */}
                {ancestors.map((ancestor) => {
                  // `ancestors` is empty in grouped mode, so this never runs
                  // there — but the row union has no ancestors to read either.
                  const parent = 'ancestors' in row ? row.ancestors[ancestor] : undefined;
                  // Absent means a cell above already spans this row.
                  const span = parentSpans.get(row.id)?.[ancestor];
                  if (span === undefined) return null;

                  const parentIssues = parent && showIssues ? nodeRowIssues(platform, parent) : [];
                  const { errors: parentErrors, warnings: parentWarnings } =
                    countSeverity(parentIssues);

                  return (
                    <TableCell
                      key={ancestor}
                      rowSpan={span}
                      data-testid={`job-parent-cell-${ancestor}-${parent?.id ?? 'none'}`}
                      // Top-aligned so a name spanning twelve rows still reads against
                      // the first of them rather than floating in the middle of the run.
                      className="overflow-hidden border-b border-r border-border bg-surface/40 px-2 py-1.5 align-top dark:bg-surface-raised/40"
                      style={{ width: `${ancestorWidth(ancestor)}px` }}
                    >
                      {parent ? (
                        // Badge pinned right so it lands in the same place on
                        // every row, whatever the name's length.
                        <span className="flex min-w-0 items-center justify-between gap-1.5">
                          <button
                            type="button"
                            onClick={() => onOpenDetails(parent)}
                            data-testid={`job-parent-${ancestor}-${parent.id}`}
                            title={t('deliveryJobReview.jobPlatformTable.openLevelSettings', {
                              level: levelLabel(platform, ancestor).toLowerCase(),
                            })}
                            className="min-w-0 truncate text-left text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {nodeFieldValue(parent, NAME_FIELD_KEY) ??
                              t('deliveryJobReview.jobPlatformTable.untitled')}
                          </button>
                          <IssueBadge
                            errors={parentErrors}
                            warnings={parentWarnings}
                            onClick={() => onOpenIssues(parent.id)}
                            testId={`job-parent-issues-${parent.id}`}
                          />
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  );
                })}

                <TableCell
                  className="border-b border-r border-border p-0"
                  style={{ width: `${previewWidth}px` }}
                >
                  <div className="flex items-center justify-center px-1 py-1.5">
                    <button
                      type="button"
                      onClick={() => onPreviewCreative(row.node)}
                      aria-label={t('deliveryJobReview.jobPlatformTable.previewCreativeFor', {
                        name:
                          nodeFieldValue(row.node, NAME_FIELD_KEY) ??
                          t('deliveryJobReview.jobPlatformTable.thisAd'),
                      })}
                      data-testid={`job-creative-${row.node.id}`}
                      className="rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <CreativeThumbnail jobId={jobId} node={row.node} />
                    </button>
                  </div>
                </TableCell>

                {columns.map((column) => (
                  <JobTableCell
                    key={column.key}
                    def={column.def}
                    field={fieldByKey.get(column.fieldKey)}
                    editable={isNodeFieldEditable(platform, row.node, column.fieldKey)}
                    issue={issueByField.get(column.fieldKey)}
                    width={column.width}
                    cellId={`${row.node.id}:${column.fieldKey}`}
                    isFocused={focusedCell === `${row.node.id}:${column.fieldKey}`}
                    preflightStatus={preflightStatus}
                    nodeId={row.node.id}
                    onCommit={commitField}
                    platformValue={platformStatusLabel(row.node, column.fieldKey)}
                  />
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </table>
    </div>
  );
}
